import React, { FC, useState } from 'react'
import styles from './Users.module.scss'
import { UserFilters } from 'models/user'
import { Button } from 'components/Button'
import { Card } from 'components/Card'
import { Heading } from 'components/Heading'
import { Text } from 'components/Text'
import { Stack } from 'components/Stack'
import { Badge } from 'components/Badge'
import { useTranslation } from 'react-i18next'
import { Search } from './components/Search'
import { InviteUserDialog } from './components/InviteUserDialog'
import { Flex } from 'components/Flex'
import { Table, TableRow, TableHead, TableBody, TableCell, TableHeadCell } from 'components/Table'
import { useFetchUsers } from 'services/user'
import { ButtonLink } from 'components/ButtonLink'
import { Spinner } from 'components/Spinner'
import { useUserContext } from 'context/user-context'

export const Users: FC = () => {
  const { t } = useTranslation()
  const [currentUser] = useUserContext()
  const [filters, setFilters] = useState<UserFilters>({ q: '' })
  const { users, status } = useFetchUsers(filters)
  const [isDialogOpen, setDialogOpen] = useState(false)

  return (
    <div className={styles.users}>
      <Stack space="small">
        <Flex justify="between" align="center">
          <Heading>{t('user.title')}</Heading>
          <Button onClick={() => setDialogOpen(true)}>{t('user.invite.cta')}</Button>
        </Flex>
        <Card className={styles.usersCard}>
          <Flex justify="between" align="center">
            <Stack space="xsmall">
              <Heading size="secondary">{t('user.card.title')}</Heading>
              <Text>{t('user.card.desc')}</Text>
            </Stack>
            <div className={styles.searchBar}>
              <Search value={filters.q} onSubmit={(value: string) => setFilters({ q: value })} />
            </div>
          </Flex>
          {status === 'loading' && (
            <Flex justify="center" align="center">
              <Spinner />
            </Flex>
          )}
          {users && (
            <Table className={styles.usersTable}>
              <TableHead>
                <TableRow>
                  <TableHeadCell>
                    <Text weight="strong" align="left">
                      {t('user.table.name')}
                    </Text>
                  </TableHeadCell>
                  <TableHeadCell>
                    <Text weight="strong" align="left">
                      {t('user.table.email')}
                    </Text>
                  </TableHeadCell>
                  <TableHeadCell>
                    <Text weight="strong" align="left">
                      {t('user.table.role')}
                    </Text>
                  </TableHeadCell>
                  <TableHeadCell></TableHeadCell>
                  <TableHeadCell></TableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Text weight="strong">{[user.firstName, user.lastName].join(' ')}</Text>
                    </TableCell>
                    <TableCell>
                      <Text weight="strong">{user.email}</Text>
                    </TableCell>
                    <TableCell>
                      <Text weight="strong">{t(`user.role.${user.role}`)}</Text>
                    </TableCell>
                    <TableCell>
                      {user.confirmed ? (
                        user.blocked && <Badge>{t(`user.status.blocked`)}</Badge>
                      ) : (
                        <Badge tone="caution">{t(`user.status.pending`)}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Flex justify="end">
                        {currentUser!.canEditUser(user) && (
                          <ButtonLink
                            size="small"
                            variant="secondary"
                            to={`/organization/users/${user.id}`}
                          >
                            {t('user.table.edit')}
                          </ButtonLink>
                        )}
                      </Flex>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </Stack>

      <InviteUserDialog
        isOpen={isDialogOpen}
        currentUser={currentUser!}
        onClose={() => setDialogOpen(false)}
      />
    </div>
  )
}

Users.displayName = 'Users'
